export const STRATEGY_VERSION_HEAD_FIELDS = [
  'name',
  'description',
  'strategyType',
  'toneOfVoice',
  'designDescription',
  'style',
  'variables',
  'tasks',
  'variableSetSourceId',
  'variableSetVersion',
  'sourceData',
  'sourceDataMapping',
  'dataUsage',
  'inputManifest',
] as const;

/** v2 records sourceData presence/absence; older snapshots cannot prove that state. */
export const STRATEGY_VERSION_HEAD_CONTENT_VERSION = 2;

export interface CliStrategyRestoreRequest {
  fromVersionId?: string;
  dryRun?: boolean;
  expectedRevision?: string;
}

export interface CliStrategyRestoreData {
  projectId: string;
  strategyId: string;
  fromVersionId: string | null;
  restored: boolean;
  dryRun: boolean;
  revision: string;
  status: 'draft';
  currentVersion: string;
  contentHash: string;
  blockCount: number;
  screenCount: number;
}

export interface CanonicalStrategyVersionBundle {
  block: Record<string, unknown>;
  screens?: Array<Record<string, unknown>>;
}

export interface CanonicalStrategyVersionContentInput {
  head: Record<string, unknown>;
  bundles: CanonicalStrategyVersionBundle[];
  theme: Record<string, unknown> | null;
}

export function pickStrategyVersionHeadContent(head: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of STRATEGY_VERSION_HEAD_FIELDS) {
    if (head[key] !== undefined) out[key] = head[key];
  }
  return out;
}

export function canonicalStrategyVersionContent(input: CanonicalStrategyVersionContentInput) {
  return {
    head: pickStrategyVersionHeadContent(input.head),
    bundles: input.bundles
      .map((bundle) => ({
        block: bundle.block,
        screens: [...(bundle.screens ?? [])]
          .sort((left, right) => documentId(left).localeCompare(documentId(right))),
      }))
      .sort((left, right) => documentId(left.block).localeCompare(documentId(right.block))),
    theme: input.theme ?? null,
  };
}

export function canonicalStrategyVersionContentJson(input: CanonicalStrategyVersionContentInput): string {
  return stableStringify(canonicalStrategyVersionContent(input));
}

function documentId(value: Record<string, unknown>): string {
  return typeof value.id === 'string' ? value.id : '';
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const object = value as Record<string, unknown>;
  const keys = Object.keys(object).sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`).join(',')}}`;
}
